import { memo, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { BiText } from 'react-icons/bi';
import { Button, Popconfirm, Popover, Typography } from 'antd';
import cn from 'classnames';
import { Attachment, List, Edit, Delete, Image, Doc2, Video, Audio } from 'refly-icons';
import type { WorkflowVariable } from '@refly/openapi-schema';
import { VariableHoverCard } from './variable-hover-card';

const { Paragraph } = Typography;

export const VARIABLE_TYPE_ICON_MAP = {
  string: BiText,
  option: List,
  resource: Attachment,
};

export const RESOURCE_TYPE_ICON_MAP = {
  image: Image,
  document: Doc2,
  video: Video,
  audio: Audio,
};

/**
 * Summarize a list of items by the category name each belongs to and produce a
 * compact inline label like `分类A(3)、未分类(2)`. Order follows first appearance.
 */
export const summarizeByCategory = <T,>(
  items: T[] | undefined,
  getCategoryName: (item: T) => string | undefined,
  uncategorizedLabel: string,
): string => {
  if (!items?.length) return '';
  const order: string[] = [];
  const counts = new Map<string, number>();
  for (const item of items) {
    const name = getCategoryName(item) || uncategorizedLabel;
    if (!counts.has(name)) {
      order.push(name);
    }
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  return order.map((name) => `${name}(${counts.get(name)})`).join('、');
};

export interface InputParameterRowProps {
  variable: WorkflowVariable;
  readonly?: boolean;
  onEdit?: (variable: WorkflowVariable) => void;
  onDelete?: (variable: WorkflowVariable) => void;
  onValueChange?: (variableId: string, value: any) => void;
  isHighlighted?: boolean;
  isPreview?: boolean;
}

export const InputParameterRow = memo(
  ({
    variable,
    readonly = false,
    onEdit,
    onDelete,
    onValueChange: _onValueChange,
    isHighlighted = false,
    isPreview = false,
  }: InputParameterRowProps) => {
    const { t } = useTranslation();
    const [isPopconfirmOpen, setIsPopconfirmOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const { name, variableType, options, value, required } = variable;
    const displayValue = useMemo(() => {
      const uncategorized = t('canvas.workflow.variables.uncategorized') || '未分类';
      if (variableType === 'option') {
        // Account selection: group by the category each account belongs to.
        const hasAccounts = value?.some((v) => v.account?.accountId);
        if (hasAccounts) {
          return summarizeByCategory(value, (v) => v.account?.categoryName, uncategorized);
        }
        // Fallback for legacy plain-option values without account metadata
        return options?.join(', ') ?? '';
      }
      if (variableType === 'resource') {
        // Single item without a category name falls back to the resource name
        if (value?.length === 1 && !value[0]?.resource?.categoryName) {
          return value[0]?.resource?.name ?? '';
        }
        return summarizeByCategory(value, (v) => v.resource?.categoryName, uncategorized);
      }
      return value?.[0]?.text ?? '';
    }, [variableType, options, value, t]);

    const handleDeleteVariable = useCallback(
      async (variable: WorkflowVariable) => {
        setIsDeleting(true);
        try {
          await onDelete?.(variable);
        } finally {
          setIsDeleting(false);
          setIsPopconfirmOpen(false);
        }
      },
      [onDelete],
    );

    const VariableIcon = useMemo(() => {
      const size = isPreview ? 16 : 14;
      if (variableType === 'option') {
        return <List size={size} color="var(--refly-text-3)" className="flex-shrink-0" />;
      }
      if (variableType === 'resource') {
        const resourceType = value?.[0]?.resource?.fileType;
        const Icon =
          RESOURCE_TYPE_ICON_MAP[resourceType as keyof typeof RESOURCE_TYPE_ICON_MAP] ?? Attachment;
        return <Icon size={size} color="var(--refly-text-3)" className="flex-shrink-0" />;
      }
      return <BiText size={size} color="var(--refly-text-3)" className="flex-shrink-0" />;
    }, [variableType, value, isPreview]);

    return (
      <Popover
        content={
          <VariableHoverCard
            variableType={variableType}
            label={name}
            options={options}
            value={value}
          />
        }
        placement="right"
        arrow={false}
        overlayClassName="variable-hover-card-popover"
        trigger="hover"
        mouseEnterDelay={0.3}
        styles={{
          body: { padding: 0, backgroundColor: 'transparent', boxShadow: 'none' },
        }}
      >
        <div
          className={cn(
            'group relative flex gap-2 items-center justify-between py-1.5 px-3 cursor-pointer hover:bg-refly-tertiary-hover transition-colors',
            isPreview
              ? 'h-[37px] border-solid border-[1px] border-refly-Card-Border rounded-xl'
              : 'h-[30px] bg-refly-bg-control-z0 rounded-[4px]',
            isHighlighted && 'bg-refly-bg-control-z1',
          )}
        >
          <div className="flex items-center flex-1 min-w-0">
            {required && <div className="text-refly-text-3 flex-shrink-0 mr-0.5">*</div>}
            <div className="flex items-center flex-1 min-w-0 overflow-hidden">
              <div
                className={cn(
                  'text-refly-func-warning-hover truncate min-w-0 flex-1 shrink basis-[80px] max-w-fit',
                  isPreview ? 'text-sm' : 'text-xs',
                )}
              >
                {name}
              </div>
              {displayValue && (
                <div
                  className={cn(
                    'text-refly-text-1 truncate ml-1 min-w-0 flex-grow-0 shrink-[100] basis-auto',
                    isPreview ? 'text-sm' : 'text-xs',
                  )}
                >
                  {displayValue}
                </div>
              )}
            </div>
          </div>

          <div className="flex-shrink-0 flex items-center gap-1">
            {VariableIcon}
            {!readonly && (
              <div
                className={cn(
                  'absolute right-0 top-0 bottom-0 items-center gap-1 px-2 rounded-r-xl bg-refly-fill-hover',
                  isPopconfirmOpen ? 'flex' : 'hidden group-hover:flex',
                )}
              >
                <Button
                  type="text"
                  size="small"
                  className="ml-1"
                  icon={<Edit size={16} />}
                  onClick={() => onEdit?.(variable)}
                />
                <Popconfirm
                  icon={null}
                  title={
                    <Paragraph
                      className="!m-0 text-[16px] font-semibold leading-[26px] p-3 max-w-[400px]"
                      ellipsis={{
                        rows: 1,
                        tooltip: (
                          <div className="max-h-[200px] overflow-y-auto">
                            {t('canvas.workflow.variables.deleteUserInput', { value: name })}
                          </div>
                        ),
                      }}
                    >
                      {t('canvas.workflow.variables.deleteUserInput', { value: name })}
                    </Paragraph>
                  }
                  description={
                    <div className="w-[400px] leading-5 px-3 pt-1 pb-2">
                      {t('canvas.workflow.variables.deleteConfirm')}
                    </div>
                  }
                  arrow={false}
                  onConfirm={() => handleDeleteVariable(variable)}
                  okText={t('common.confirm')}
                  cancelText={t('common.cancel')}
                  onOpenChange={setIsPopconfirmOpen}
                  okButtonProps={{ loading: isDeleting, className: 'w-20 h-8 mb-3 mr-3' }}
                  cancelButtonProps={{ className: 'w-20 h-8 mb-3' }}
                  placement="topRight"
                >
                  <Button
                    type="text"
                    size="small"
                    icon={<Delete size={16} />}
                    className={isPopconfirmOpen ? 'bg-refly-tertiary-hover' : ''}
                  />
                </Popconfirm>
              </div>
            )}
          </div>
        </div>
      </Popover>
    );
  },
);

InputParameterRow.displayName = 'InputParameterRow';
